// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedicationRequest, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';

export interface UsePatientPrescriptionsResult {
  prescriptions: MedicationRequest[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

export function usePatientPrescriptions(patient: Patient | undefined): UsePatientPrescriptionsResult {
  const medplum = useMedplum();
  const [prescriptions, setPrescriptions] = useState<MedicationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const results = await medplum.searchResources(
        'MedicationRequest',
        `patient=Patient/${patient.id}&_sort=-authoredon&_count=100`,
        { cache: 'no-cache' }
      );
      const loaded = Array.from(results) as MedicationRequest[];
      setPrescriptions(loaded);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load prescriptions'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  return { prescriptions, loading, error, refresh: fetch };
}
